import React, { useCallback, useState } from 'react';
import { ReactFlow, Node, Edge, Background, MarkerType, Position, useNodesState, useEdgesState } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Plus, Minus, X, ChevronDown, ChevronRight, Calendar, BarChart2, Users, Briefcase, PlusCircle, Clock, Activity, HelpCircle, ChevronUp, Info, Download } from 'lucide-react';
type MindMapNode = {
  id: string;
  type: 'skill' | 'connection' | 'category';
  label: string;
  description?: string;
  children?: string[];
  level?: number; // For skills
  department?: string; // For connections
  dateAdded?: string; // When the node was added
  ghost?: boolean; // Whether this is a ghost node
  suggestedDescription?: string; // Description for ghost nodes
  activityLevel?: number; // 0-100 scale for how active/relevant this skill or connection is
  lastUsed?: string; // When the skill or connection was last used
};
// Activity level to color mapping
const getActivityColor = (type: 'skill' | 'connection', activityLevel: number = 0) => {
  // For skills (blue scale)
  if (type === 'skill') {
    if (activityLevel >= 80) return {
      bg: '#bfdbfe',
      border: '#3b82f6'
    }; // High activity - bright blue
    if (activityLevel >= 50) return {
      bg: '#dbeafe',
      border: '#60a5fa'
    }; // Medium activity - medium blue
    return {
      bg: '#eff6ff',
      border: '#93c5fd'
    }; // Low activity - light blue
  }
  // For connections (green scale)
  if (activityLevel >= 80) return {
    bg: '#a7f3d0',
    border: '#10b981'
  }; // High activity - bright green
  if (activityLevel >= 50) return {
    bg: '#d1fae5',
    border: '#34d399'
  }; // Medium activity - medium green
  return {
    bg: '#ecfdf5',
    border: '#6ee7b7'
  }; // Low activity - light green
};
const BranchingMindMap = () => {
  const [zoom, setZoom] = useState(1);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [nodeNotes, setNodeNotes] = useState<Record<string, string>>({});
  const [showDetails, setShowDetails] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState<Node | null>(null);
  const [showLegend, setShowLegend] = useState(false);
  // Initial mind map data structure
  const initialMindMap: MindMapNode[] = [{
    id: 'root',
    type: 'category',
    label: 'Professional Growth',
    children: ['skills', 'connections']
  }, {
    id: 'skills',
    type: 'category',
    label: 'Skills',
    children: ['technical-skills', 'soft-skills']
  }, {
    id: 'technical-skills',
    type: 'category',
    label: 'Technical Skills',
    children: ['programming', 'data-analysis', 'cloud-computing-ghost']
  }, {
    id: 'programming',
    type: 'skill',
    label: 'Programming',
    description: 'JavaScript, TypeScript, React',
    level: 3,
    dateAdded: '2023-10-15T14:30:00',
    children: ['javascript', 'typescript', 'react', 'vue-ghost'],
    activityLevel: 90,
    lastUsed: '2023-12-28T09:15:00'
  }, {
    id: 'javascript',
    type: 'skill',
    label: 'JavaScript',
    description: 'Core language skills',
    level: 3,
    dateAdded: '2023-10-16T10:30:00',
    activityLevel: 95,
    lastUsed: '2023-12-29T14:20:00'
  }, {
    id: 'typescript',
    type: 'skill',
    label: 'TypeScript',
    description: 'Type-safe JavaScript',
    level: 2,
    dateAdded: '2023-10-20T14:45:00',
    activityLevel: 85,
    lastUsed: '2023-12-27T11:45:00'
  }, {
    id: 'react',
    type: 'skill',
    label: 'React',
    description: 'UI library',
    level: 3,
    dateAdded: '2023-10-18T09:15:00',
    activityLevel: 90,
    lastUsed: '2023-12-29T10:30:00'
  }, {
    id: 'vue-ghost',
    type: 'skill',
    label: 'Vue.js',
    description: 'Progressive JavaScript framework',
    ghost: true,
    suggestedDescription: 'Complementary to your React skills',
    level: 1
  }, {
    id: 'cloud-computing-ghost',
    type: 'skill',
    label: 'Cloud Computing',
    description: 'AWS, Azure, GCP',
    ghost: true,
    suggestedDescription: 'Recommended based on your technical profile',
    level: 1
  }, {
    id: 'data-analysis',
    type: 'skill',
    label: 'Data Analysis',
    description: 'SQL, Python, Visualization',
    level: 2,
    dateAdded: '2023-11-02T09:45:00',
    activityLevel: 40,
    lastUsed: '2023-12-10T15:30:00'
  }, {
    id: 'soft-skills',
    type: 'category',
    label: 'Soft Skills',
    children: ['communication', 'leadership', 'negotiation-ghost']
  }, {
    id: 'communication',
    type: 'skill',
    label: 'Communication',
    description: 'Written and verbal communication skills',
    level: 3,
    dateAdded: '2023-09-20T11:15:00',
    activityLevel: 75,
    lastUsed: '2023-12-22T13:45:00'
  }, {
    id: 'leadership',
    type: 'skill',
    label: 'Leadership',
    description: 'Team management and project leadership',
    level: 2,
    dateAdded: '2023-12-05T16:20:00',
    activityLevel: 60,
    lastUsed: '2023-12-18T09:30:00'
  }, {
    id: 'negotiation-ghost',
    type: 'skill',
    label: 'Negotiation',
    description: 'Conflict resolution and deal-making',
    ghost: true,
    suggestedDescription: 'Complements your leadership skills',
    level: 1
  }, {
    id: 'connections',
    type: 'category',
    label: 'Connections',
    children: ['internal-connections', 'external-connections', 'community-ghost']
  }, {
    id: 'internal-connections',
    type: 'category',
    label: 'Internal',
    children: ['manager', 'team-member', 'product-manager-ghost']
  }, {
    id: 'manager',
    type: 'connection',
    label: 'Sarah Chen',
    description: 'Engineering Director, Mentor',
    department: 'Engineering',
    dateAdded: '2023-08-12T10:30:00',
    activityLevel: 85,
    lastUsed: '2023-12-28T11:00:00'
  }, {
    id: 'team-member',
    type: 'connection',
    label: 'Alex Johnson',
    description: 'Senior Developer, Technical Lead',
    department: 'Engineering',
    dateAdded: '2023-10-05T14:15:00',
    activityLevel: 95,
    lastUsed: '2023-12-29T15:30:00'
  }, {
    id: 'product-manager-ghost',
    type: 'connection',
    label: 'Jamie Taylor',
    description: 'Product Manager',
    department: 'Product',
    ghost: true,
    suggestedDescription: 'Recommended for cross-functional collaboration'
  }, {
    id: 'external-connections',
    type: 'category',
    label: 'External',
    children: ['industry-contact', 'conference-contact-ghost']
  }, {
    id: 'industry-contact',
    type: 'connection',
    label: 'Michael Rodriguez',
    description: 'Product Manager, Industry Expert',
    department: 'Product',
    dateAdded: '2023-11-18T09:45:00',
    activityLevel: 30,
    lastUsed: '2023-12-05T14:00:00'
  }, {
    id: 'conference-contact-ghost',
    type: 'connection',
    label: 'Lisa Wong',
    description: 'Technical Architect',
    department: 'Technology',
    ghost: true,
    suggestedDescription: 'Met at React Conference 2023'
  }, {
    id: 'community-ghost',
    type: 'category',
    label: 'Community',
    ghost: true,
    suggestedDescription: 'Expand your network through community involvement',
    children: []
  }];
  // Calculate positions for mind map layout
  const calculateNodes = () => {
    const nodeMap = new Map<string, MindMapNode>();
    initialMindMap.forEach(node => nodeMap.set(node.id, node));
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    // Recursive function to position nodes
    const positionNode = (nodeId: string, x: number, y: number, level: number, parentId?: string) => {
      const node = nodeMap.get(nodeId)!;
      const children = node.children || [];
      const isGhost = node.ghost === true;
      // Add node
      const nodeType = node.type;
      // Get colors based on activity level
      let nodeColor = '#f3f4f6'; // Default for categories
      let nodeBorder = '#d1d5db'; // Default for categories
      if (nodeType === 'skill' || nodeType === 'connection') {
        const activityColors = getActivityColor(nodeType, node.activityLevel);
        nodeColor = activityColors.bg;
        nodeBorder = activityColors.border;
      }
      // Special styling for ghost nodes
      const ghostStyle = isGhost ? {
        opacity: 0.6,
        borderStyle: 'dashed',
        background: nodeType === 'skill' ? '#e0f2fe' : nodeType === 'connection' ? '#ecfdf5' : '#f9fafb'
      } : {};
      nodes.push({
        id: nodeId,
        data: {
          label: node.label,
          description: node.description,
          type: node.type,
          level: node.level,
          department: node.department,
          dateAdded: node.dateAdded,
          ghost: isGhost,
          suggestedDescription: node.suggestedDescription,
          activityLevel: node.activityLevel,
          lastUsed: node.lastUsed
        },
        position: {
          x,
          y
        },
        style: {
          background: nodeColor,
          border: `1px solid ${nodeBorder}`,
          borderRadius: node.type === 'category' ? '4px' : '8px',
          padding: '10px',
          width: Math.max(150, node.label.length * 8),
          fontSize: level === 0 ? '16px' : level === 1 ? '14px' : '12px',
          fontWeight: level < 2 ? 'bold' : 'normal',
          ...ghostStyle
        }
      });
      // Add edge from parent
      if (parentId) {
        edges.push({
          id: `e-${parentId}-${nodeId}`,
          source: parentId,
          target: nodeId,
          style: {
            stroke: nodeBorder,
            strokeDasharray: isGhost ? '5,5' : 'none',
            opacity: isGhost ? 0.6 : 1
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: nodeBorder
          }
        });
      }
      // Position children - horizontal layout (children to the right of parent)
      if (children.length > 0) {
        const childSpacing = 180; // Vertical spacing between siblings
        const totalHeight = (children.length - 1) * childSpacing;
        const startY = y - totalHeight / 2;
        const childX = x + 250; // Horizontal distance to children
        children.forEach((childId, index) => {
          const childY = startY + index * childSpacing;
          positionNode(childId, childX, childY, level + 1, nodeId);
        });
      }
    };
    // Start positioning from root - place it on the left side
    positionNode('root', 50, 300, 0);
    return {
      nodes,
      edges
    };
  };
  const {
    nodes: initialNodes,
    edges: initialEdges
  } = calculateNodes();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  // Track enabled ghost nodes
  const [enabledGhosts, setEnabledGhosts] = useState<string[]>([]);
  const handleNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    setSelectedNode(node.id);
    setShowDetails(false); // Reset details visibility when selecting a new node
  }, []);
  const closeNodeDetails = () => {
    setSelectedNode(null);
  };
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.1, 2));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.1, 0.5));
  };
  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };
  const toggleLegend = () => {
    setShowLegend(!showLegend);
  };
  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (selectedNode) {
      setNodeNotes({
        ...nodeNotes,
        [selectedNode]: e.target.value
      });
    }
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const openConnectionGraph = (node: Node) => {
    setSelectedConnection(node);
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
    setSelectedConnection(null);
  };
  const enableGhostNode = (nodeId: string) => {
    // Update the list of enabled ghosts
    setEnabledGhosts(prev => [...prev, nodeId]);
    // Update the node's style to make it solid
    setNodes(nodes.map(node => {
      if (node.id === nodeId) {
        const nodeType = node.data.type;
        const activityColors = getActivityColor(nodeType, 50); // Default medium activity for new nodes
        return {
          ...node,
          data: {
            ...node.data,
            ghost: false,
            dateAdded: new Date().toISOString(),
            activityLevel: 50,
            lastUsed: new Date().toISOString()
          },
          style: {
            ...node.style,
            opacity: 1,
            borderStyle: 'solid',
            background: activityColors.bg,
            border: `1px solid ${activityColors.border}`
          }
        };
      }
      return node;
    }));
    // Update the edges to make them solid
    setEdges(edges.map(edge => {
      if (edge.target === nodeId) {
        return {
          ...edge,
          style: {
            ...edge.style,
            strokeDasharray: 'none',
            opacity: 1
          }
        };
      }
      return edge;
    }));
  };
  // Format activity level as text
  const getActivityText = (level?: number) => {
    if (!level) return 'Low Activity';
    if (level >= 80) return 'High Activity';
    if (level >= 50) return 'Medium Activity';
    return 'Low Activity';
  };
  // Generate connection graph data
  const generateConnectionGraph = (connectionNode: Node) => {
    const connectionName = connectionNode.data.label;
    const department = connectionNode.data.department || 'Unknown';
    // Create nodes for the connection graph
    const graphNodes: Node[] = [
    // Center node - the connection
    {
      id: 'person',
      data: {
        label: connectionName,
        type: 'connection'
      },
      position: {
        x: 250,
        y: 200
      },
      style: {
        background: '#d1fae5',
        border: '2px solid #6ee7b7',
        borderRadius: '50%',
        padding: '15px',
        width: 120,
        height: 120,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        fontSize: '14px',
        fontWeight: 'bold'
      }
    },
    // Department node
    {
      id: 'department',
      data: {
        label: department,
        icon: 'department'
      },
      position: {
        x: 450,
        y: 100
      },
      style: {
        background: '#fef3c7',
        border: '1px solid #fbbf24',
        borderRadius: '8px',
        padding: '10px',
        width: 150
      }
    },
    // Skills nodes
    {
      id: 'skill1',
      data: {
        label: 'Leadership',
        level: 3,
        icon: 'skill'
      },
      position: {
        x: 100,
        y: 100
      },
      style: {
        background: '#dbeafe',
        border: '1px solid #93c5fd',
        borderRadius: '8px',
        padding: '10px',
        width: 150
      }
    }, {
      id: 'skill2',
      data: {
        label: 'Communication',
        level: 2,
        icon: 'skill'
      },
      position: {
        x: 100,
        y: 300
      },
      style: {
        background: '#dbeafe',
        border: '1px solid #93c5fd',
        borderRadius: '8px',
        padding: '10px',
        width: 150
      }
    },
    // Connections
    {
      id: 'connection1',
      data: {
        label: 'Team Member',
        icon: 'connection'
      },
      position: {
        x: 450,
        y: 300
      },
      style: {
        background: '#d1fae5',
        border: '1px solid #6ee7b7',
        borderRadius: '8px',
        padding: '10px',
        width: 150
      }
    }];
    // Create edges
    const graphEdges: Edge[] = [{
      id: 'e-person-department',
      source: 'person',
      target: 'department',
      animated: true,
      style: {
        stroke: '#fbbf24'
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#fbbf24'
      }
    }, {
      id: 'e-person-skill1',
      source: 'person',
      target: 'skill1',
      animated: true,
      style: {
        stroke: '#93c5fd'
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#93c5fd'
      }
    }, {
      id: 'e-person-skill2',
      source: 'person',
      target: 'skill2',
      animated: true,
      style: {
        stroke: '#93c5fd'
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#93c5fd'
      }
    }, {
      id: 'e-person-connection1',
      source: 'person',
      target: 'connection1',
      animated: true,
      style: {
        stroke: '#6ee7b7'
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#6ee7b7'
      }
    }];
    return {
      nodes: graphNodes,
      edges: graphEdges
    };
  };
  const handleExport = () => {
    // This is just a placeholder for now
    alert('Export functionality will be implemented in a future update.');
  };
  const selectedNodeData = nodes.find(node => node.id === selectedNode);
  return <div className="flex flex-col h-full border rounded-lg bg-white shadow-sm overflow-hidden relative" data-id="element-159">
      <div className="px-4 py-3 border-b bg-gray-50" data-id="element-160">
        <div className="flex justify-between items-center" data-id="element-161">
          <div data-id="element-162">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-163">Branching</h2>
            <p className="text-sm text-gray-500" data-id="element-164">
              View your skills and connections as a mind map
            </p>
            <div className="mt-1 text-xs text-gray-500 italic" data-id="element-165">
              Connections are enabled through the Growth Agent
            </div>
          </div>
          <button onClick={handleExport} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center text-sm" data-id="element-166">
            <Download className="w-4 h-4 mr-2" data-id="element-167" />
            Export
          </button>
        </div>
      </div>
      <div className="flex-1 relative" data-id="element-168">
        <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onNodeClick={handleNodeClick} zoomOnScroll={false} zoomOnPinch={false} zoomOnDoubleClick={false} panOnScroll={true} defaultZoom={zoom} minZoom={0.5} maxZoom={2} fitView data-id="element-169">
          <Background color="#f9fafb" gap={16} data-id="element-170" />
        </ReactFlow>
        {/* Zoom controls at the bottom-left */}
        <div className="absolute bottom-4 left-4 z-10 flex flex-col space-y-2" data-id="element-171">
          <button onClick={handleZoomIn} className="bg-white border border-gray-300 rounded-full p-2 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" title="Zoom in" data-id="element-172">
            <Plus className="w-5 h-5 text-blue-600" data-id="element-173" />
          </button>
          <button onClick={handleZoomOut} className="bg-white border border-gray-300 rounded-full p-2 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500" title="Zoom out" data-id="element-174">
            <Minus className="w-5 h-5 text-blue-600" data-id="element-175" />
          </button>
        </div>
        {/* Legend toggle button at the bottom-right */}
        <div className="absolute bottom-4 right-4 z-10" data-id="element-176">
          <button onClick={toggleLegend} className="bg-white border border-gray-300 rounded-full p-2 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center" title="Show legend" data-id="element-177">
            <Info className="w-5 h-5 text-blue-600" data-id="element-178" />
          </button>
        </div>
        {/* Legend modal */}
        {showLegend && <div className="absolute bottom-16 right-4 z-20 bg-white border rounded-lg shadow-md p-4 w-64" data-id="element-179">
            <div className="flex justify-between items-center mb-3" data-id="element-180">
              <div className="text-sm font-medium text-gray-700" data-id="element-181">Legend</div>
              <button onClick={toggleLegend} className="text-gray-400 hover:text-gray-600 focus:outline-none" title="Close legend" data-id="element-182">
                <X className="w-4 h-4" data-id="element-183" />
              </button>
            </div>
            {/* Node types */}
            <div className="mb-3" data-id="element-184">
              <div className="text-xs font-medium text-gray-700 mb-1.5" data-id="element-185">
                Node Types
              </div>
              <div className="flex items-center mb-1.5" data-id="element-186">
                <div className="w-3 h-3 rounded-sm bg-blue-100 border border-blue-300 mr-2" data-id="element-187"></div>
                <span className="text-xs text-gray-600" data-id="element-188">Skills</span>
              </div>
              <div className="flex items-center mb-1.5" data-id="element-189">
                <div className="w-3 h-3 rounded-sm bg-green-100 border border-green-300 mr-2" data-id="element-190"></div>
                <span className="text-xs text-gray-600" data-id="element-191">Connections</span>
              </div>
              <div className="flex items-center mb-1.5" data-id="element-192">
                <div className="w-3 h-3 rounded-sm bg-gray-100 border border-dashed border-gray-300 opacity-60 mr-2" data-id="element-193"></div>
                <span className="text-xs text-gray-600" data-id="element-194">Suggested (Ghost)</span>
              </div>
            </div>
            {/* Activity levels */}
            <div data-id="element-195">
              <div className="text-xs font-medium text-gray-700 mb-1.5" data-id="element-196">
                Activity Levels
              </div>
              <div className="flex items-center mb-1.5" data-id="element-197">
                <div className="w-3 h-3 rounded-sm bg-blue-300 border border-blue-500 mr-2" data-id="element-198"></div>
                <span className="text-xs text-gray-600" data-id="element-199">
                  High Activity (Skills)
                </span>
              </div>
              <div className="flex items-center mb-1.5" data-id="element-200">
                <div className="w-3 h-3 rounded-sm bg-green-300 border border-green-500 mr-2" data-id="element-201"></div>
                <span className="text-xs text-gray-600" data-id="element-202">
                  High Activity (Connections)
                </span>
              </div>
              <div className="flex items-center" data-id="element-203">
                <div className="w-3 h-3 rounded-sm bg-blue-50 border border-blue-200 mr-2" data-id="element-204"></div>
                <span className="text-xs text-gray-600" data-id="element-205">Low Activity</span>
              </div>
            </div>
          </div>}
        {selectedNodeData && <div className="absolute top-4 right-4 z-10 bg-white border rounded-lg shadow-lg p-4 w-72" data-id="element-206">
            <div className="flex justify-between items-start mb-2" data-id="element-207">
              <h3 className="font-medium" data-id="element-208">{selectedNodeData.data.label}</h3>
              <button onClick={closeNodeDetails} className="text-gray-400 hover:text-gray-600" data-id="element-209">
                <X className="w-4 h-4" data-id="element-210" />
              </button>
            </div>
            <div className="space-y-3 text-sm" data-id="element-211">
              {selectedNodeData.data.type !== 'category' && <div className="flex items-center" data-id="element-212">
                  <span className={`px-2 py-1 rounded text-xs mr-2 ${selectedNodeData.data.type === 'skill' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`} data-id="element-213">
                    {selectedNodeData.data.type === 'skill' ? 'Skill' : 'Connection'}
                  </span>
                  {selectedNodeData.data.ghost && <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs" data-id="element-214">
                      Suggested
                    </span>}
                  {selectedNodeData.data.level && !selectedNodeData.data.ghost && <div className="flex ml-1" data-id="element-215">
                        {[...Array(3)].map((_, i) => <div key={i} className={`w-2 h-2 rounded-full mx-0.5 ${i < selectedNodeData.data.level! ? 'bg-blue-500' : 'bg-gray-200'}`} data-id="element-216" />)}
                      </div>}
                  {selectedNodeData.data.department && <span className="text-gray-600 text-xs ml-1" data-id="element-217">
                      {selectedNodeData.data.department}
                    </span>}
                </div>}
              {/* Activity level indicator */}
              {selectedNodeData.data.type !== 'category' && !selectedNodeData.data.ghost && selectedNodeData.data.activityLevel !== undefined && <div className="flex items-center" data-id="element-218">
                    <Activity className="w-3 h-3 text-gray-500 mr-1" data-id="element-219" />
                    <span className="text-xs text-gray-600 mr-2" data-id="element-220">
                      Activity:
                    </span>
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-221">
                      <div className={`h-full ${selectedNodeData.data.activityLevel >= 80 ? 'bg-green-500' : selectedNodeData.data.activityLevel >= 50 ? 'bg-blue-500' : 'bg-gray-400'}`} style={{
                width: `${selectedNodeData.data.activityLevel}%`
              }} data-id="element-222"></div>
                    </div>
                    <span className="text-xs font-medium ml-2" data-id="element-223">
                      {getActivityText(selectedNodeData.data.activityLevel)}
                    </span>
                  </div>}
              {selectedNodeData.data.description && <p className="text-gray-600" data-id="element-224">
                  {selectedNodeData.data.description}
                </p>}
              {/* Suggested description for ghost nodes */}
              {selectedNodeData.data.ghost && selectedNodeData.data.suggestedDescription && <div className="bg-blue-50 p-2 rounded border border-blue-100 text-blue-700 text-xs" data-id="element-225">
                    {selectedNodeData.data.suggestedDescription}
                  </div>}
              {/* Enable button for ghost nodes */}
              {selectedNodeData.data.ghost && <button onClick={() => enableGhostNode(selectedNodeData.id)} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-md py-1.5 text-xs flex items-center justify-center mt-1" data-id="element-226">
                  <PlusCircle className="w-3 h-3 mr-1.5" data-id="element-227" />
                  Enable {selectedNodeData.data.type}
                </button>}
              {/* Details section */}
              {selectedNodeData.data.type !== 'category' && !selectedNodeData.data.ghost && <div className="border-t pt-2" data-id="element-228">
                    <button onClick={toggleDetails} className="flex items-center text-gray-700 text-xs hover:text-gray-900 w-full justify-between" data-id="element-229">
                      <span className="flex items-center" data-id="element-230">
                        <Calendar className="w-3 h-3 mr-1" data-id="element-231" />
                        Details
                      </span>
                      {showDetails ? <ChevronDown className="w-3 h-3" data-id="element-232" /> : <ChevronRight className="w-3 h-3" data-id="element-233" />}
                    </button>
                    {showDetails && <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded" data-id="element-234">
                        <p data-id="element-235">
                          Added: {formatDate(selectedNodeData.data.dateAdded)}
                        </p>
                        {selectedNodeData.data.lastUsed && <p className="mt-1 flex items-center" data-id="element-236">
                            <Clock className="w-3 h-3 mr-1 text-gray-500" data-id="element-237" />
                            Last used:{' '}
                            {formatDate(selectedNodeData.data.lastUsed)}
                          </p>}
                      </div>}
                  </div>}
              {/* Notes section */}
              {selectedNodeData.data.type !== 'category' && !selectedNodeData.data.ghost && <div className="border-t pt-2" data-id="element-238">
                    <label className="block text-xs text-gray-700 mb-1" data-id="element-239">
                      Notes
                    </label>
                    <textarea value={nodeNotes[selectedNode] || ''} onChange={handleNotesChange} placeholder="Add your notes here..." className="w-full border rounded p-2 text-xs h-20 focus:ring-blue-500 focus:border-blue-500" data-id="element-240" />
                  </div>}
              {/* View graph button for connections */}
              {selectedNodeData.data.type === 'connection' && !selectedNodeData.data.ghost && <div className="border-t pt-2" data-id="element-241">
                    <button className="text-blue-600 text-xs hover:text-blue-800 flex items-center" onClick={() => openConnectionGraph(selectedNodeData)} data-id="element-242">
                      <BarChart2 className="w-3 h-3 mr-1" data-id="element-243" />
                      View graph
                    </button>
                  </div>}
            </div>
          </div>}
        {/* Connection Graph Modal */}
        {showModal && selectedConnection && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-244">
            <div className="bg-white rounded-lg shadow-xl w-4/5 max-w-4xl h-4/5 flex flex-col" data-id="element-245">
              <div className="p-4 border-b flex justify-between items-center" data-id="element-246">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-247">
                  {selectedConnection.data.label}'s Network
                </h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600" data-id="element-248">
                  <X className="w-5 h-5" data-id="element-249" />
                </button>
              </div>
              <div className="flex-1 p-4" data-id="element-250">
                <div className="flex h-full" data-id="element-251">
                  {/* Graph visualization */}
                  <div className="flex-1 border rounded-lg overflow-hidden" data-id="element-252">
                    <ReactFlow nodes={generateConnectionGraph(selectedConnection).nodes} edges={generateConnectionGraph(selectedConnection).edges} fitView attributionPosition="bottom-right" data-id="element-253">
                      <Background color="#f9fafb" gap={16} data-id="element-254" />
                    </ReactFlow>
                  </div>
                  {/* Sidebar with details */}
                  <div className="w-64 ml-4 border rounded-lg p-4 bg-gray-50" data-id="element-255">
                    <h4 className="font-medium mb-3" data-id="element-256">
                      {selectedConnection.data.label}
                    </h4>
                    <div className="space-y-4 text-sm" data-id="element-257">
                      {selectedConnection.data.department && <div data-id="element-258">
                          <div className="text-gray-500 mb-1 flex items-center" data-id="element-259">
                            <Briefcase className="w-3 h-3 mr-1" data-id="element-260" />
                            Department
                          </div>
                          <div className="font-medium" data-id="element-261">
                            {selectedConnection.data.department}
                          </div>
                        </div>}
                      <div data-id="element-262">
                        <div className="text-gray-500 mb-1 flex items-center" data-id="element-263">
                          <Users className="w-3 h-3 mr-1" data-id="element-264" />
                          Network
                        </div>
                        <div className="font-medium" data-id="element-265">3 connections</div>
                      </div>
                      <div data-id="element-266">
                        <div className="text-gray-500 mb-1" data-id="element-267">Top Skills</div>
                        <ul className="list-disc list-inside" data-id="element-268">
                          <li data-id="element-269">Leadership</li>
                          <li data-id="element-270">Communication</li>
                        </ul>
                      </div>
                      {selectedConnection.data.activityLevel !== undefined && <div data-id="element-271">
                          <div className="text-gray-500 mb-1 flex items-center" data-id="element-272">
                            <Activity className="w-3 h-3 mr-1" data-id="element-273" />
                            Activity Level
                          </div>
                          <div className="flex items-center" data-id="element-274">
                            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-275">
                              <div className={`h-full ${selectedConnection.data.activityLevel >= 80 ? 'bg-green-500' : selectedConnection.data.activityLevel >= 50 ? 'bg-blue-500' : 'bg-gray-400'}`} style={{
                          width: `${selectedConnection.data.activityLevel}%`
                        }} data-id="element-276"></div>
                            </div>
                            <span className="text-xs font-medium ml-2" data-id="element-277">
                              {getActivityText(selectedConnection.data.activityLevel)}
                            </span>
                          </div>
                        </div>}
                      {selectedConnection.data.lastUsed && <div data-id="element-278">
                          <div className="text-gray-500 mb-1 flex items-center" data-id="element-279">
                            <Clock className="w-3 h-3 mr-1" data-id="element-280" />
                            Last Interaction
                          </div>
                          <div className="font-medium" data-id="element-281">
                            {formatDate(selectedConnection.data.lastUsed)}
                          </div>
                        </div>}
                      <div className="border-t pt-3 mt-3" data-id="element-282">
                        <div className="text-gray-500 mb-1" data-id="element-283">Notes</div>
                        <p className="italic text-gray-600" data-id="element-284">
                          {nodeNotes[selectedConnection.id] || 'No notes added yet.'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>}
      </div>
    </div>;
};
export default BranchingMindMap;